import React, { useEffect, useState } from 'react';
import {
  withStyles,
} from '@material-ui/core';
import PF2MDataTable from '~/components/PF2MDataTable';
import { useTranslation } from 'react-i18next';
import {
  deallocateLoadAllocations,
  getLoadMaterialMappings,
  getMaterialsMap,
  updateLoadAllocations,
} from '~/store/dispatch/actions';
import { useDispatch, useSelector } from 'react-redux';
import styles from '../styles';
import ReloadQueue from '../../ReloadQueue';

const AllocationsEditTabs = () => {
  const { t: translate } = useTranslation();
  const dispatch = useDispatch();
  const [loadedData, setLoadedData] = useState(false);
  const excavatorOrigins = useSelector(
    state => state.dispatch.excavatorOrigins,
  ).filter(item => item.id);
  const truckAllocations = useSelector(
    state => state.dispatch.truckAllocations,
  ).filter(item => item.id);
  const loadAllocations = useSelector(
    state => state.dispatch.loadAllocations,
  ).filter(r => r.id);
  const loadEquipments = useSelector(state => state.nref.equipments)
    .filter(r => [1, 15].includes(r.id_equip));
  const elements = useSelector(state => state.nref.elements);
  const subElements = useSelector(state => state.nref.subElements);
  const materials = useSelector(state => state.dispatch.materialsMap);

  const [equipsDeleted, setEquipsDeleted] = useState([]);

  // eslint-disable-next-line no-underscore-dangle
  const roles = useSelector(state => state.auth.user._roles);

  useEffect(() => {
    dispatch(getLoadMaterialMappings());

    if (!loadedData) {
      ReloadQueue.stopReload();
      dispatch(getMaterialsMap());
      setLoadedData(true);
    }
  }, [loadedData, dispatch]);

  const materialMappings2 = useSelector(state => state.dispatch.loadMaterialMappings)
    .filter(r => r.load_allocation_id != null);

  const loadAllocationCounts = truckAllocations.reduce((acc, { load_allocation_id }) => {
    acc[load_allocation_id] = (acc[load_allocation_id] || 0) + 1;
    return acc;
  }, {});

  // Contagem dos Trucks alocados
  const equipmentMaterialMapping = materialMappings2.reduce((acc, current) => {
    const equipId = current.equip_id;
    if (!acc[equipId]) {
      acc[equipId] = [];
    }
    acc[equipId].push(current);
    return acc;
  }, {});

  const loadAllocationMappings = loadAllocations.reduce((acc, current) => {
    const equipId = current.equip_id;
    if (!acc[equipId]) {
      acc[equipId] = [];
    }
    acc[equipId].push(current);
    return acc;
  }, {});

  // Mapeamento dos dados da tabela
  const excavatorOriginsMapped = excavatorOrigins.map((excavator) => {
    const loadMaterialMappings = equipmentMaterialMapping[excavator.equip_id] || [{}];
    const loadAllocationMappingLoads = loadAllocationMappings[excavator.equip_id] || [{}];

    const firstLoadMaterialMapping = loadMaterialMappings[0];
    const secondLoadMaterialMapping = loadMaterialMappings[1];
    const firstLoadAllocationMappingLoad = loadAllocationMappingLoads[0];
    const trucks = truckAllocations.map(truck => ({
      load_allocation_id: truck.load_allocation_id,
      truck_id: truck.equip_id,
      equip_group_id: truck.equip_group_id,
    })).filter(truck => truck.load_allocation_id === excavator.id);

    return {
      ...excavator,
      trucks,
      loadMaterialMappings,
      loadAllocationMappingLoads,
      origin_id:
        firstLoadAllocationMappingLoad.origin_element_id,
      origin_point_id:
        firstLoadAllocationMappingLoad.origin_element_point_id,
      dynamic_allocation:
        firstLoadAllocationMappingLoad.dynamic_allocation || false,
      dynamic_config_id:
        firstLoadAllocationMappingLoad.dynamic_configuration?.id || null,
      group_id_dynamic:
        firstLoadAllocationMappingLoad.dynamic_configuration?.group_id || null,
      group_name_dynamic:
        firstLoadAllocationMappingLoad.dynamic_configuration?.group_name,
      max_allocation_capacity:
        firstLoadAllocationMappingLoad.dynamic_configuration?.max_allocation_capacity,
      max_load_queue_time:
        firstLoadAllocationMappingLoad.dynamic_configuration?.max_load_queue_time,
      mean_queue_time_range:
        firstLoadAllocationMappingLoad.dynamic_configuration?.mean_queue_time_range,
      min_permanent_allocation_cycles:
        firstLoadAllocationMappingLoad.dynamic_configuration?.min_permanent_allocation_cycles,
      max_temporary_allocation_cycles:
        firstLoadAllocationMappingLoad.dynamic_configuration?.max_temporary_allocation_cycles,
      id_material_mapping:
        firstLoadMaterialMapping.id,
      material_id:
        firstLoadMaterialMapping.material_id,
      first_element_id:
        firstLoadMaterialMapping.first_element_id,
      first_element_point_id:
        firstLoadMaterialMapping.first_element_point_id,
      second_element_id:
        firstLoadMaterialMapping.second_element_id,
      second_element_point_id:
        firstLoadMaterialMapping.second_element_point_id,
      number_of_trucks_allocated: loadAllocationCounts[excavator.id] || 0,
      id_material_mapping_two:
        secondLoadMaterialMapping?.id,
      material_id_two:
        secondLoadMaterialMapping?.material_id,
      first_element_id_two:
        secondLoadMaterialMapping?.first_element_id,
      first_element_point_id_two:
        secondLoadMaterialMapping?.first_element_point_id,
      second_element_id_two:
        secondLoadMaterialMapping?.second_element_id,
      second_element_point_id_two:
        secondLoadMaterialMapping?.second_element_point_id,
      number_of_trucks_allocated_two: loadAllocationCounts[excavator.id] || 0,
    };
  });

  const hasDynamicPermission = roles.some(role => role.module_value === 'DYNAMIC');

  const isDynamicValidated = (row) => {
    if (row.dynamic_allocation) {
      return true;
    }
    return false;
  };

  const validateMaterialsTwo = (row) => {
    if (row.id_material_mapping_two) {
      return true;
    }
    return false;
  };

  const columns = [
    {
      title: translate('common:DynamicAllocation'),
      field: 'dynamic_allocation',
      customType: 'boolean',
      editable: hasDynamicPermission ? 'always' : 'never',
      required: !!hasDynamicPermission,
    },
    {
      editable: 'never',
      title: translate('common:Priority'),
      field: 'priority',
    },
    {
      editable: (_columnDef, rowData) => isDynamicValidated(rowData),
      title: translate('common:AllocationGroup'),
      field: 'group_name_dynamic',
      required: !!hasDynamicPermission,
    },
    {
      editable: 'never',
      title: translate('common:LoadingEquipments'),
      field: 'equip_id',
      lookup: loadEquipments,
      customType: 'filterLookup',
      required: true,
    },
    {
      editable: 'always',
      title: translate('common:Origin'),
      field: 'origin_id',
      lookup: elements,
      customType: 'filterLookup',
      required: true,
    },
    {
      editable: 'always',
      title: translate('common:SubOrigin'),
      field: 'origin_point_id',
      lookup: subElements,
      customType: 'filterLookup',
      lookupFilter: (option, row) => (
        String(option.id_element) === String(row.origin_id)
        || String(row.origin_id) === '0'
      ),
      required: true,
    },
    {
      editable: 'never',
      title: translate('common:NumberOfTrucksAllocated'),
      field: 'number_of_trucks_allocated',
      required: true,
    },
    {
      editable: (_columnDef, rowData) => isDynamicValidated(rowData),
      title: translate('common:MaxCapacityOfAllocations'),
      field: 'max_allocation_capacity',
      required: !!hasDynamicPermission,
      type: 'numeric',
    },
    {
      editable: (_columnDef, rowData) => isDynamicValidated(rowData),
      title: translate('common:MaxTimeLoadingQueue'),
      field: 'max_load_queue_time',
      required: !!hasDynamicPermission,
      type: 'numeric',
    },
    {
      editable: (_columnDef, rowData) => isDynamicValidated(rowData),
      title: translate('common:AssessmentInterval'),
      field: 'mean_queue_time_range',
      required: !!hasDynamicPermission,
      type: 'numeric',
    },
    {
      editable: 'always',
      title: translate('common:Material'),
      field: 'material_id',
      lookup: Array.from(materials.reduce((acc, material) => {
        if (!acc.has(material.id_material)) {
          acc.set(material.id_material, {
            id: material.id_material,
            name: material.name,
            id_destinations: [material.id_destination],
          });
        } else {
          acc.get(material.id_material).id_destinations.push(material.id_destination);
        }
        return acc;
      }, new Map()).values()),
      customType: 'filterLookup',
      lookupFilter: (option, row) => (
        option.id_destinations.includes(parseInt(row.origin_element_point_id))
        || row.origin_element_point_id === '0'
      ),
      required: true,
    },
    {
      editable: 'always',
      title: `${translate('common:DestinationElement')} 1`,
      field: 'first_element_id',
      lookup: elements,
      customType: 'filterLookup',
      required: true,
    },
    {
      editable: 'always',
      title: `${translate('common:DestinationSubElement')} 1`,
      field: 'first_element_point_id',
      lookup: subElements,
      customType: 'filterLookup',
      lookupFilter: (option, row) => (
        String(option.id_element) === String(row.first_element_id)
        || String(row.first_element_id) === '0'
      ),
      required: true,
    },
    {
      editable: 'always',
      title: `${translate('common:DestinationElement')} 2`,
      field: 'second_element_id',
      lookup: elements,
      customType: 'filterLookup',
      required: true,
    },
    {
      editable: 'always',
      title: `${translate('common:DestinationSubElement')} 2`,
      field: 'second_element_point_id',
      lookup: subElements,
      customType: 'filterLookup',
      lookupFilter: (option, row) => (
        String(option.id_element) === String(row.second_element_id)
        || String(row.second_element_id) === '0'
      ),
      required: true,
    },
    {
      editable: (_columnDef, rowData) => validateMaterialsTwo(rowData),
      title: translate('common:Material'),
      field: 'material_id_two',
      lookup: Array.from(materials.reduce((acc, material) => {
        if (!acc.has(material.id_material)) {
          acc.set(material.id_material, {
            id: material.id_material,
            name: material.name,
            id_destinations: [material.id_destination],
          });
        } else {
          acc.get(material.id_material).id_destinations.push(material.id_destination);
        }
        return acc;
      }, new Map()).values()),
      customType: 'filterLookup',
      lookupFilter: (option, row) => (
        option.id_destinations.includes(parseInt(row.origin_element_point_id))
        || row.origin_element_point_id === '0'
      ),
    },
    {
      editable: (_columnDef, rowData) => validateMaterialsTwo(rowData),
      title: `${translate('common:DestinationElement')} 1`,
      field: 'first_element_id_two',
      lookup: elements,
      customType: 'filterLookup',
    },
    {
      editable: (_columnDef, rowData) => validateMaterialsTwo(rowData),
      title: `${translate('common:DestinationSubElement')} 1`,
      field: 'first_element_point_id_two',
      lookup: subElements,
      customType: 'filterLookup',
      lookupFilter: (option, row) => (
        String(option.id_element) === String(row.first_element_id_two)
        || String(row.first_element_id_two) === '0'
      ),
    },
    {
      editable: (_columnDef, rowData) => validateMaterialsTwo(rowData),
      title: `${translate('common:DestinationElement')} 2`,
      field: 'second_element_id_two',
      lookup: elements,
      customType: 'filterLookup',
    },
    {
      editable: (_columnDef, rowData) => validateMaterialsTwo(rowData),
      title: `${translate('common:DestinationSubElement')} 2`,
      field: 'second_element_point_id_two',
      lookup: subElements,
      customType: 'filterLookup',
      lookupFilter: (option, row) => (
        String(option.id_element) === String(row.second_element_id_two)
        || String(row.second_element_id_two) === '0'
      ),
    },
  ];

  const prepareAndUpdateLoadAllocations = (row) => {
    const materialMappings = [];

    // Sempre adiciona o primeiro objeto
    materialMappings.push({
      id: row.id_material_mapping,
      equip_id: row.equip_id,
      equip_name: row.equip_name,
      material_id: row.material_id,
      material_name:
        materials.find(m => m.id_material === row.material_id)?.name || '',
      first_element_id: row.first_element_id,
      first_element_name:
        elements.find(e => e.id === row.first_element_id)?.name || '',
      first_element_point_id: row.first_element_point_id,
      first_element_point_name:
        subElements.find(se => se.id === row.first_element_point_id)?.name || '',
      second_element_id: row.second_element_id,
      second_element_name:
        elements.find(e => e.id === row.second_element_id)?.name || '',
      second_element_point_id: row.second_element_point_id,
      second_element_point_name:
        subElements.find(se => se.id === row.second_element_point_id)?.name || '',
      load_allocation_id: row.id,
      active: true,
      update_status: 'U',
    });

    // Adiciona o segundo objeto somente se o id_material_mapping_two existir
    if (row.id_material_mapping_two) {
      materialMappings.push({
        id: row.id_material_mapping_two,
        equip_id: row.equip_id,
        equip_name: row.equip_name,
        material_id: row.material_id_two,
        material_name:
          materials.find(m => m.id_material === row.material_id_two)?.name || '',
        first_element_id: row.first_element_id_two,
        first_element_name:
          elements.find(e => e.id === row.first_element_id_two)?.name || '',
        first_element_point_id: row.first_element_point_id_two,
        first_element_point_name:
          subElements.find(se => se.id === row.first_element_point_id_two)?.name || '',
        second_element_id: row.second_element_id_two,
        second_element_name:
          elements.find(e => e.id === row.second_element_id_two)?.name || '',
        second_element_point_id: row.second_element_point_id_two,
        second_element_point_name:
          subElements.find(se => se.id === row.second_element_point_id_two)?.name || '',
        load_allocation_id: row.id,
        active: true,
        update_status: 'U',
      });
    }

    const truckIds = row.trucks.map(truck => truck.truck_id);
    const truckGroupIds = row.trucks.map(truck => truck.equip_group_id);
    const loadAllocation = {
      id: row.id,
      equip_type_id: row.equip_type_id,
      equip_group_id: row.equip_group_id,
      equip_id: row.equip_id,
      equip_name: row.equip_name,
      priority: row.priority,
      origin_element_id: row.origin_id,
      origin_element_point_id: row.origin_point_id,
      origin_element_point_name: subElements.find(se => se.id === row.origin_point_id)?.name || '',
      dynamic_allocation: row.dynamic_allocation,
      dynamic_configuration: {
        group_id: row.group_id_dynamic,
        id: row.dynamic_config_id,
        max_load_queue_time: row.max_load_queue_time || 0,
        min_permanent_allocation_cycles: row.min_permanent_allocation_cycles || 0,
        load_allocation_id: row.id,
        max_allocation_capacity: row.max_allocation_capacity || 0,
        mean_queue_time_range: row.mean_queue_time_range || 0,
        max_temporary_allocation_cycles: row.max_temporary_allocation_cycles || 0,
        min_allocation_capacity: row.min_allocation_capacity || 1,
        group_name: row.group_name_dynamic || '',
      },
      status: row.status,
      truck_groups: truckGroupIds,
      trucks: truckIds,
      materials: [row.material_id],
    };

    dispatch(updateLoadAllocations({
      item: loadAllocation,
      materialMappings,
      finallyCb: () => dispatch(getLoadMaterialMappings()),
    }));
  };

  return (
    <>
      <PF2MDataTable
        title=""
        options={{
          showTitle: true,
          search: true,
          selection: true,
          onDelete: true,
          hideAdd: true,
        }}
        onDiscard={() => dispatch(getLoadMaterialMappings())}
        onChange={(newData) => {
          newData.forEach(prepareAndUpdateLoadAllocations);
        }}
        onDelete={(newData) => {
          const allocationIds = newData.map(item => item.id);
          const newEquipIds = newData.map(item => item.equip_id);

          setEquipsDeleted(prevEquipIds => [...prevEquipIds, ...newEquipIds]);

          allocationIds.forEach((id) => {
            dispatch(deallocateLoadAllocations({ id })).then(() => {
              dispatch(getLoadMaterialMappings());
            });
          });
        }}
        columns={columns}
        data={excavatorOriginsMapped.filter(
          excavator => !equipsDeleted.includes(excavator.equip_id),
        )}
      />
    </>
  );
};

AllocationsEditTabs.defaultProps = {};

export default withStyles(styles)(AllocationsEditTabs);
